import { useCallback, useEffect, useRef } from "react";

import { Snackbar } from "@js/components/snackbar";
import { useLatestCallback } from "@js/hooks/use-latest-callback";

import { useExistingJobData } from "../use-existing-job-data";

export const useCopyJobData = ({ copyJobId }: { copyJobId?: number }) => {
  const {
    jobData: jobToCopy,
    isFetching: isFetchingJob,
    refetchJob,
  } = useExistingJobData(copyJobId);

  const isGenerationTaskInProgress =
    !!jobToCopy?.generating_ai_interview_data_task_in_progress;
  const isFetchingJobToCopy = isGenerationTaskInProgress || isFetchingJob;

  const timeoutRef = useRef<number>(undefined);
  const refetchJobCallback = useLatestCallback(refetchJob);
  const pollJob = useCallback(async () => {
    try {
      const response = await refetchJobCallback();
      const isStillGenerating =
        response.generating_ai_interview_data_task_in_progress;
      if (!isStillGenerating) {
        return;
      }

      // eslint-disable-next-line react-hooks/immutability
      timeoutRef.current = window.setTimeout(() => pollJob(), 3000);
    } catch (_error) {
      Snackbar.error("Something went wrong during job sync.");
    }
  }, [refetchJobCallback]);

  const isPollingInitializedRef = useRef(false);
  useEffect(() => {
    if (!jobToCopy || isPollingInitializedRef.current) {
      return;
    }

    if (!jobToCopy.generating_ai_interview_data_task_in_progress) {
      return;
    }

    isPollingInitializedRef.current = true;
    timeoutRef.current = window.setTimeout(() => pollJob(), 2000);
  }, [jobToCopy, pollJob]);

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  return { jobToCopy, isFetchingJobToCopy };
};
